/**
 * Assigned courses on the profile.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import { Box, Tabs, Tab, Grid, TextField } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { VText, VButton } from "form";
import { useAsterController } from "context";
import { getCourseImageUrl } from "utils/string";
import { BPopOver, BThumb } from "components";

/**
 * Filterable list of courses currently assigned to the learner.
 */
const ProfileContentDone = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { mobile, courses, loadedDashboard } = controller;

  const [data, setData] = useState([]);
  const [tab, setTab] = useState(0);
  const [term, setTerm] = useState("");

  const showList = useMemo(() => {
    const tmp = term.toLowerCase();
    return (data || []).filter((v) => v.display_name?.toLowerCase()?.includes(tmp));
  }, [data, term]);

  useEffect(() => {
    if (loadedDashboard) {
      const currDate = new Date();
      setData((courses.data || []).filter((v) => v.enrollment_status && new Date(v.enroll_end) > currDate && new Date(v.enroll_start) < currDate));
    }
  }, [courses.data, loadedDashboard]);
  useEffect(() => mobile && setTab(0), [mobile]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  return (
    <>
      <Box className="md:px-14 px-4 py-8">
        <Box className="pb-6 flex" id="v-user-title">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} className="w-full md:flex hidden justify-center md:justify-start">
              <VText className="text-2xl">{data.filter((course) => course.enrollment_status).length || 0}</VText>&nbsp;
              <VText color="secondary" className="text-2xl">
                {t("Courses in progress")}
              </VText>
            </Grid>
            <Grid item xs={12} md={8} className="flex justify-end flex-wrap items-center">
              <VButton variant="text" color="secondary" className="text-sm normal-case" onClick={() => setTerm("")} disabled={!term}>
                <Icon icon="akar-icons:circle-check" />
                &nbsp;{t("All status")}
              </VButton>
              <BPopOver
                className="ml-2"
                autoClose={false}
                trigger={
                  <VButton variant="text" color="secondary" className="text-sm normal-case">
                    <Icon icon="ant-design:filter-filled" />
                    &nbsp;{t("Filter")}
                  </VButton>
                }
              >
                <TextField className="w-64" size="small" value={term} onChange={(e) => setTerm(e.target.value)} />
              </BPopOver>
              <Box className="md:flex hidden h-8 ml-2 v-tab-switch">
                <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue}>
                  <Tab icon={<Icon className="mr-0" icon="akar-icons:grid" />} />
                  <Tab icon={<Icon className="mr-0" icon="ci:list-checklist" />} />
                </Tabs>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {tab === 0 && (
          <Box>
            <Grid container spacing={3}>
              {showList.map((course) => (
                <Grid key={course.course_index} item xs={12} sm={6} md={4} xl={3}>
                  <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 h-full flex flex-col">
                    <Box className="flex flex-col w-full">
                      <BThumb src={getCourseImageUrl(course.course_image_url)} className="w-full aspect-[9/5] rounded-md" />
                    </Box>
                    <Box className="p-4 flex flex-col gap-1 h-full">
                      <VText className="text-xl text-limit-2">{course.display_name || "-"}</VText>
                      <VText className="text-limit-1" div>
                        {course.short_description || "-"}
                      </VText>
                      <VText color="secondary" className="flex items-center text-sm mb-4">
                        <Icon icon="lucide:clock-9" />
                        &nbsp;{course.enroll_start}
                      </VText>
                      <Link to={`/course/${course.id}`} className="no-underline flex items-center mt-auto">
                        <VButton variant="contained" color="primary" className="w-full normal-case">
                          {t("Continue")}
                        </VButton>
                      </Link>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {tab === 1 && (
          <Box>
            <Grid container spacing={3}>
              {showList.map((course) => (
                <Grid key={course.course_index} item xs={12}>
                  <Box className="flex flex-row justify-between rounded border border-solid border-gray-200 dark:border-gray-500 pr-4">
                    <Box className="flex flex-row gap-4 items-center">
                      <BThumb src={getCourseImageUrl(course.course_image_url)} className="aspect-[9/5] rounded-md h-32 w-56 flex-shrink-0" />
                      <Box className="py-4 flex flex-col w-full gap-0.5">
                        <VText className="text-xl text-limit-1">{course.display_name || "-"}</VText>
                        <VText className="text-limit-1">{course.short_description || "-"}</VText>
                        <VText color="secondary" className="flex items-center text-sm">
                          <Icon icon="lucide:clock-9" />
                          &nbsp;{course.enroll_start}
                        </VText>
                      </Box>
                    </Box>
                    <Box className="flex flex-col justify-center w-3/12">
                      <Link to={`/course/${course.id}`} className="no-underline flex items-center mt-4">
                        <VButton variant="contained" color="primary" className="w-full normal-case">
                          {t("Continue")}
                        </VButton>
                      </Link>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ProfileContentDone;
